import { colorAt, METRICS, type ReviewMetric } from '@motogo/shared';
import { type FC, useCallback, useEffect, useRef } from 'react';

/** Visible time window (epoch-ms) — shared across the stacked charts so they zoom together. */
export interface ChartView {
  t0: number;
  t1: number;
}

interface Props {
  /** Epoch-ms per point (shared across the stacked charts so cursors line up). */
  times: number[];
  /** Display-space values per point (kph, g, deg…); null = gap (no data at that fix). */
  values: (number | null)[];
  scrubIndex: number | null;
  onScrub: (index: number) => void;
  metric: ReviewMetric;
  /** Visible X window (shared, in epoch-ms). */
  view: ChartView;
  onViewChange: (t0: number, t1: number) => void;
  onResetView: () => void;
}

const HEIGHT = 96;
const PAD_TOP = 16;
const PAD_BOTTOM = 6;
const PAD_X = 4;
const DRAG_THRESHOLD = 6; // px before a finger drag commits to scrub (horizontal) or scroll (vertical)

/**
 * One metric over the *time* axis, canvas-drawn (GGDiagram precedent). The trace is coloured
 * per segment by the metric's ramp — the same one the map line uses, so chart and map read
 * alike. All stacked traces share `times`, `view` + `scrubIndex`, so a single cursor crosses
 * them and one zoom window applies to all. The Y scale stays fixed to the whole ride (only X
 * zooms) so absolute magnitude/sign stays comparable while inspecting a window.
 *
 * Gestures: one-finger horizontal drag (or mouse drag) scrubs; a tap sets the cursor; a
 * vertical finger drag falls through to scroll the panel (`touch-action: pan-y`); two-finger
 * pinch or the mouse wheel zooms the shared X window; double-click resets it.
 * See CLAUDE.md → Ride Review.
 */
const RideTraceChart: FC<Props> = ({ times, values, scrubIndex, onScrub, metric, view, onViewChange, onResetView }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Gesture bookkeeping (refs — must survive re-renders without re-binding handlers).
  const pointers = useRef(new Map<number, number>()); // pointerId → clientX
  const mode = useRef<'none' | 'pending' | 'scrub'>('none');
  const downPos = useRef<{ x: number; y: number } | null>(null);
  // Pinch anchor: the times grabbed under each of the two fingers when the pinch began.
  const pinch = useRef<{ idA: number; idB: number; tA: number; tB: number } | null>(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !wrap || !ctx || times.length < 2) return;

    const { label, unit, mode: scaleMode } = METRICS[metric];
    const w = wrap.clientWidth;
    const h = HEIGHT;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const t0 = view.t0;
    const span = view.t1 - t0 || 1;
    const innerW = w - 2 * PAD_X;
    const xAt = (i: number) => PAD_X + ((times[i] - t0) / span) * innerW;

    // Y scale fixed to the whole ride (not the visible window) so magnitude stays comparable.
    const finite = values.filter((v): v is number => v != null);
    const maxAbs = Math.max(1e-6, ...finite.map((v) => Math.abs(v)));
    const maxVal = Math.max(1e-6, ...finite);
    const plotTop = PAD_TOP;
    const plotBottom = h - PAD_BOTTOM;
    const yAt = (v: number) =>
      scaleMode === 'centered'
        ? (plotTop + plotBottom) / 2 - (v / maxAbs) * ((plotBottom - plotTop) / 2)
        : plotBottom - (v / maxVal) * (plotBottom - plotTop);

    // Zero / baseline reference line
    const zeroY = scaleMode === 'centered' ? (plotTop + plotBottom) / 2 : plotBottom;
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(PAD_X, zeroY);
    ctx.lineTo(w - PAD_X, zeroY);
    ctx.stroke();

    // Trace — coloured per segment by the metric ramp (matches the map). Break across nulls,
    // skip segments wholly outside the visible window.
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    for (let i = 1; i < times.length; i++) {
      const v0 = values[i - 1];
      const v1 = values[i];
      if (v0 == null || v1 == null) continue;
      if ((times[i - 1] < t0 && times[i] < t0) || (times[i - 1] > view.t1 && times[i] > view.t1)) continue;
      ctx.strokeStyle = colorAt(metric, (v0 + v1) / 2);
      ctx.beginPath();
      ctx.moveTo(xAt(i - 1), yAt(v0));
      ctx.lineTo(xAt(i), yAt(v1));
      ctx.stroke();
    }

    // Label
    ctx.fillStyle = '#9aa1ab';
    ctx.font = '600 10px system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(label, PAD_X + 2, 2);

    // Cursor — only when its moment falls inside the visible window.
    if (scrubIndex != null && scrubIndex >= 0 && scrubIndex < times.length) {
      const ts = times[scrubIndex];
      if (ts >= t0 && ts <= view.t1) {
        const x = xAt(scrubIndex);
        ctx.strokeStyle = 'rgba(255,255,255,0.5)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, PAD_TOP - 6);
        ctx.lineTo(x, plotBottom);
        ctx.stroke();

        const v = values[scrubIndex];
        if (v != null) {
          ctx.beginPath();
          ctx.arc(x, yAt(v), 4, 0, Math.PI * 2);
          ctx.fillStyle = '#ffffff';
          ctx.fill();

          const onRight = x > w - 72;
          ctx.fillStyle = '#f2f3f5';
          ctx.font = '600 11px system-ui, sans-serif';
          ctx.textAlign = onRight ? 'right' : 'left';
          const shown = Math.abs(v) >= 10 ? Math.round(v).toString() : v.toFixed(2).replace(/\.?0+$/, '');
          ctx.fillText(`${shown}${unit}`, onRight ? x - 6 : x + 6, 2);
        }
      }
    }
  }, [times, values, scrubIndex, metric, view]);

  useEffect(() => {
    draw();
  }, [draw]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => draw());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [draw]);

  const indexFromX = useCallback(
    (clientX: number): number => {
      const canvas = canvasRef.current;
      if (!canvas || times.length === 0) return 0;
      const rect = canvas.getBoundingClientRect();
      const innerW = rect.width - 2 * PAD_X;
      const frac = Math.min(1, Math.max(0, (clientX - rect.left - PAD_X) / innerW));
      const target = view.t0 + frac * (view.t1 - view.t0);
      let best = 0;
      let bestD = Infinity;
      for (let i = 0; i < times.length; i++) {
        const d = Math.abs(times[i] - target);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
      return best;
    },
    [times, view],
  );

  // Fraction (0..1) of the plot width at a client X — may fall outside [0,1] off the canvas.
  const fracOf = useCallback((clientX: number): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const rect = canvas.getBoundingClientRect();
    return (clientX - rect.left - PAD_X) / (rect.width - 2 * PAD_X);
  }, []);

  // Clamp a desired window to the ride bounds, preserving its span where possible.
  const applyView = useCallback(
    (t0: number, span: number) => {
      const fullT0 = times[0];
      const fullT1 = times[times.length - 1];
      const fullSpan = fullT1 - fullT0 || 1;
      const minSpan = Math.max(1500, fullSpan / 400);
      const s = Math.min(fullSpan, Math.max(minSpan, span));
      let n0 = t0;
      let n1 = n0 + s;
      if (n0 < fullT0) {
        n0 = fullT0;
        n1 = n0 + s;
      }
      if (n1 > fullT1) {
        n1 = fullT1;
        n0 = Math.max(fullT0, n1 - s);
      }
      onViewChange(n0, n1);
    },
    [times, onViewChange],
  );

  // Wheel = zoom around the cursor. Bound natively (non-passive) so preventDefault sticks.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      if (times.length < 2) return;
      e.preventDefault();
      const frac = Math.min(1, Math.max(0, fracOf(e.clientX)));
      const span = view.t1 - view.t0;
      const tAt = view.t0 + frac * span;
      const nextSpan = span * (e.deltaY > 0 ? 1.2 : 1 / 1.2);
      applyView(tAt - frac * nextSpan, nextSpan);
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [view, times, fracOf, applyView]);

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    pointers.current.set(e.pointerId, e.clientX);

    if (pointers.current.size === 2) {
      // Second finger down → begin pinch; anchor the time under each finger.
      const [idA, idB] = [...pointers.current.keys()];
      const xA = pointers.current.get(idA)!;
      const xB = pointers.current.get(idB)!;
      pinch.current = {
        idA,
        idB,
        tA: view.t0 + fracOf(xA) * (view.t1 - view.t0),
        tB: view.t0 + fracOf(xB) * (view.t1 - view.t0),
      };
      mode.current = 'none'; // abandon any in-progress scrub
      return;
    }

    downPos.current = { x: e.clientX, y: e.clientY };
    if (e.pointerType === 'mouse') {
      mode.current = 'scrub';
      e.currentTarget.setPointerCapture(e.pointerId);
      onScrub(indexFromX(e.clientX));
    } else {
      mode.current = 'pending'; // wait for intent (horizontal scrub vs vertical scroll)
    }
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, e.clientX);

    if (pinch.current) {
      const { idA, idB, tA, tB } = pinch.current;
      const xA = pointers.current.get(idA);
      const xB = pointers.current.get(idB);
      if (xA == null || xB == null) return;
      const fA = fracOf(xA);
      const fB = fracOf(xB);
      if (Math.abs(fB - fA) < 1e-3) return;
      const span = (tB - tA) / (fB - fA); // keep both grabbed times under their fingers
      applyView(tA - fA * span, span);
      return;
    }

    if (mode.current === 'pending' && downPos.current) {
      const dx = e.clientX - downPos.current.x;
      const dy = e.clientY - downPos.current.y;
      if (Math.abs(dx) > DRAG_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        mode.current = 'scrub';
        e.currentTarget.setPointerCapture(e.pointerId);
        onScrub(indexFromX(e.clientX));
      } else if (Math.abs(dy) > DRAG_THRESHOLD) {
        mode.current = 'none'; // vertical intent → let the panel scroll (touch-action: pan-y)
      }
      return;
    }

    if (mode.current === 'scrub') onScrub(indexFromX(e.clientX));
  }

  function handlePointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    const wasTap = mode.current === 'pending';
    pointers.current.delete(e.pointerId);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* not captured */
    }

    if (pinch.current && pointers.current.size < 2) {
      pinch.current = null;
      mode.current = 'none';
      downPos.current = null;
      return;
    }
    if (wasTap && pointers.current.size === 0) onScrub(indexFromX(e.clientX));
    if (pointers.current.size === 0) {
      mode.current = 'none';
      downPos.current = null;
    }
  }

  return (
    <div ref={wrapRef} className="mg-chart-wrap">
      <canvas
        ref={canvasRef}
        className="mg-chart-canvas"
        style={{ height: HEIGHT }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={onResetView}
      />
    </div>
  );
};

export default RideTraceChart;
